import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Upload, CheckCircle, AlertTriangle, ArrowRight, 
  Eye, EyeOff, ZoomIn, Clock, MapPin, Cpu, ChevronLeft, ChevronRight,
  Send, Users
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { aedApi } from '../utils/api';
import { STATUS_CONFIG, AED_STATUSES, AED_STATUS_ORDER, formatConfidence, formatTimestamp } from '../utils/constants';
import { AuthImage } from './AuthImage';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';

const ConfidenceBar = ({ label, value, showPercent = true }) => {
  const percentage = Math.round(value * 100);
  const color = percentage >= 85 ? 'bg-emerald-500' : percentage >= 70 ? 'bg-amber-500' : 'bg-red-500';
  
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-400 w-32 truncate">{label}</span>
      <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showPercent && (
        <span className="text-xs font-mono text-slate-500 w-10 text-right">{percentage}%</span>
      )}
    </div>
  );
};

const ImageViewer = ({ imageUrl, analysis, viewMode, setViewMode }) => {
  const placeholderImage = "https://images.unsplash.com/photo-1617257484989-9f8ab411c362?w=800&auto=format&fit=crop";
  const isAuthImage = imageUrl && imageUrl.includes('aed-images/download');
  
  return (
    <div className="relative rounded-xl overflow-hidden border border-slate-700 bg-slate-900">
      {/* View mode toggle */}
      <div className="absolute top-3 left-3 z-10 flex gap-1 p-1 bg-slate-900/80 rounded-lg backdrop-blur-sm">
        <button
          onClick={() => setViewMode('original')}
          className={`px-3 py-1.5 text-xs font-mono rounded transition-all ${
            viewMode === 'original' 
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50' 
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <Eye className="w-3 h-3 inline mr-1" />
          Original
        </button>
        <button
          onClick={() => setViewMode('overlay')}
          className={`px-3 py-1.5 text-xs font-mono rounded transition-all ${
            viewMode === 'overlay' 
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50' 
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <Cpu className="w-3 h-3 inline mr-1" />
          AI Overlay
        </button>
        <button
          onClick={() => setViewMode('zoom')}
          className={`px-3 py-1.5 text-xs font-mono rounded transition-all ${
            viewMode === 'zoom' 
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50' 
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <ZoomIn className="w-3 h-3 inline mr-1" />
          LED Zoom
        </button>
      </div>

      {/* Image */}
      <div className="relative aspect-video">
        {isAuthImage ? (
          <AuthImage
            src={imageUrl}
            alt="AED Monitoring"
            className={`w-full h-full object-cover transition-all duration-300 ${
              viewMode === 'zoom' ? 'scale-150 translate-y-8' : ''
            }`}
          />
        ) : (
          <img 
            src={imageUrl || placeholderImage} 
            alt="AED Monitoring"
            className={`w-full h-full object-cover transition-all duration-300 ${
              viewMode === 'zoom' ? 'scale-150 translate-y-8' : ''
            }`}
          />
        )}
        
        {/* AI Overlay */}
        {viewMode === 'overlay' && analysis && (
          <div className="absolute inset-0 pointer-events-none">
            {/* AED Bounding Box */}
            {analysis.detected_aed && (
              <div 
                className="absolute border-2 border-cyan-400 rounded-lg"
                style={{ 
                  left: '15%', top: '10%', 
                  width: '70%', height: '80%',
                  boxShadow: '0 0 10px rgba(6, 182, 212, 0.5)'
                }}
              >
                <span className="absolute -top-6 left-0 text-[10px] font-mono bg-cyan-500/80 text-black px-2 py-0.5 rounded">
                  AED DETECTED {formatConfidence(analysis.aed_present_confidence)}
                </span>
              </div>
            )}
            
            {/* LED Indicator */}
            {analysis.led_visible ? (
              <div 
                className="absolute border-2 border-emerald-400 rounded"
                style={{ 
                  left: '60%', top: '15%', 
                  width: '15%', height: '12%',
                  boxShadow: '0 0 15px rgba(16, 185, 129, 0.6)'
                }}
              >
                <span className="absolute -top-5 left-0 text-[9px] font-mono bg-emerald-500/80 text-black px-1.5 py-0.5 rounded">
                  LED: {analysis.led_state?.toUpperCase()}
                </span>
              </div>
            ) : (
              <div 
                className="absolute border-2 border-dashed border-amber-400 rounded"
                style={{ 
                  left: '60%', top: '15%', 
                  width: '15%', height: '12%'
                }}
              >
                <span className="absolute -top-5 left-0 text-[9px] font-mono bg-amber-500/80 text-black px-1.5 py-0.5 rounded">
                  LED NOT VISIBLE
                </span>
              </div>
            )}
            
            {/* Scan lines effect */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent animate-pulse" />
          </div>
        )}
      </div>
      
      {/* No image indicator */}
      {!imageUrl && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
          <p className="text-sm text-slate-500">No image uploaded yet</p>
        </div>
      )}
    </div>
  );
};

const HistoryCarousel = ({ history, onSelect }) => {
  const scrollRef = useRef(null);
  
  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ 
        left: direction * 200, 
        behavior: 'smooth' 
      });
    }
  };

  if (!history || history.length === 0) {
    return (
      <div className="text-center py-4 text-sm text-slate-500">
        No image history available
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => scroll(-1)}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1 bg-slate-900/80 rounded-full text-slate-400 hover:text-white"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      
      <div 
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto px-6 py-2 custom-scrollbar"
        style={{ scrollbarWidth: 'none' }}
      >
        {history.map((item, i) => (
          <button
            key={i}
            onClick={() => onSelect(item)}
            className="flex-shrink-0 relative rounded-lg overflow-hidden border border-slate-700 hover:border-cyan-500/50 transition-all"
          >
            <img 
              src={item.image_url || "https://images.unsplash.com/photo-1617257484989-9f8ab411c362?w=100"} 
              alt={`History ${i + 1}`}
              className="w-20 h-14 object-cover"
            />
            <span className="absolute bottom-0 left-0 right-0 text-[8px] font-mono bg-slate-900/80 text-slate-400 px-1 py-0.5 truncate">
              {formatTimestamp(item.timestamp)}
            </span>
          </button>
        ))}
      </div>
      
      <button
        onClick={() => scroll(1)}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-1 bg-slate-900/80 rounded-full text-slate-400 hover:text-white"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

export const AEDDetailModal = () => {
  const { selectedAED, isDetailOpen, closeDetail, triggerRefresh } = useApp();
  const [viewMode, setViewMode] = useState('original');
  const [history, setHistory] = useState([]);
  const [comment, setComment] = useState('');
  const [overrideStatus, setOverrideStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [currentAED, setCurrentAED] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (selectedAED) {
      setCurrentAED(selectedAED);
      setComment('');
      setOverrideStatus('');
      fetchHistory(selectedAED.id);
    }
  }, [selectedAED]);

  const fetchHistory = async (aedId) => {
    try {
      const data = await aedApi.getHistory(aedId);
      setHistory(data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentAED) return;

    setUploading(true);
    try {
      const result = await aedApi.analyzeImage(currentAED.id, file);
      setCurrentAED(result.aed);
      toast.success('Image analyzed successfully');
      await fetchHistory(currentAED.id);
      triggerRefresh();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to analyze image');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!currentAED) return;
    setProcessing(true);
    try {
      await aedApi.confirm(currentAED.id, comment);
      toast.success('Status confirmed');
      triggerRefresh();
      closeDetail();
    } catch (error) {
      toast.error('Failed to confirm status');
    } finally {
      setProcessing(false);
    }
  };

  const handleOverride = async () => {
    if (!currentAED || !overrideStatus || !comment) {
      toast.error('Please select a status and add a comment');
      return;
    }
    setProcessing(true);
    try {
      await aedApi.override(currentAED.id, overrideStatus, comment);
      toast.success('Status overridden');
      triggerRefresh();
      closeDetail();
    } catch (error) {
      toast.error('Failed to override status');
    } finally {
      setProcessing(false);
    }
  };

  const handleEscalate = async () => {
    if (!currentAED) return;
    setProcessing(true);
    try {
      await aedApi.escalate(currentAED.id, comment);
      toast.success('Escalated to field team');
      triggerRefresh();
      closeDetail();
    } catch (error) {
      toast.error('Failed to escalate');
    } finally {
      setProcessing(false);
    }
  };

  if (!currentAED) return null;

  const analysis = currentAED.last_analysis;
  const statusConfig = STATUS_CONFIG[currentAED.current_status] || STATUS_CONFIG.UNKNOWN;
  // Single source for expiration confidence so breakdown text and ConfidenceBar always match (fixes 85% vs 100% mismatch)
  const expirationConfidence = analysis?.expiration_detected_confidence ?? (/expir|expired/i.test(analysis?.reason || '') ? (currentAED?.ai_confidence ?? 0) : 0);

  return (
    <Dialog open={isDetailOpen} onOpenChange={closeDetail}>
      <DialogContent 
        className="max-w-6xl w-[95vw] h-[90vh] p-0 bg-slate-950 border-slate-800 overflow-hidden"
        data-testid="aed-detail-modal"
      >
        {/* Header */}
        <DialogHeader className="p-4 border-b border-slate-800 bg-slate-900/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <DialogTitle className="font-heading text-xl font-bold tracking-wide text-white">
                {currentAED.aed_id}
              </DialogTitle>
              <Badge className={`${statusConfig.bgColor} ${statusConfig.color} font-mono`}>
                {statusConfig.label}
              </Badge>
              {currentAED.review_required && (
                <Badge variant="outline" className="bg-amber-500/20 border-amber-500/50 text-amber-400">
                  NEEDS REVIEW
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {currentAED.location}
              </span>
              <span className="font-mono text-xs text-slate-500">
                {currentAED.subscriber}
              </span>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Left: Image Analysis */}
            <div className="space-y-4">
              <ImageViewer 
                imageUrl={currentAED.last_image_url}
                analysis={analysis}
                viewMode={viewMode}
                setViewMode={setViewMode}
              />
              
              {/* Upload Button */}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  data-testid="upload-image-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/50 text-cyan-400"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Analyzing...' : 'Upload New Image'}
                </Button>
              </div>

              {/* History Carousel */}
              <div className="glass rounded-lg p-4">
                <h3 className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  Image History (Last 7 Days)
                </h3>
                <HistoryCarousel 
                  history={history}
                  onSelect={(item) => {
                    if (item.analysis) {
                      setCurrentAED(prev => ({
                        ...prev,
                        last_image_url: item.image_url,
                        last_analysis: item.analysis
                      }));
                    }
                  }}
                />
              </div>
            </div>

            {/* Right: AI Analysis & Controls */}
            <div className="space-y-4">
              <Tabs defaultValue="analysis" className="w-full">
                <TabsList className="w-full bg-slate-900 border border-slate-800">
                  <TabsTrigger value="analysis" className="flex-1">AI Analysis</TabsTrigger>
                  <TabsTrigger value="action" className="flex-1">Take Action</TabsTrigger>
                </TabsList>
                
                <TabsContent value="analysis" className="space-y-4 mt-4">
                  {/* AI Decision Summary */}
                  <div className="glass rounded-lg p-4 tech-corner">
                    <h3 className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
                      <Cpu className="w-3 h-3 text-cyan-400" />
                      AI Decision Summary
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <span className="text-[10px] text-slate-500 uppercase">Recommended Status</span>
                        <p className={`text-lg font-mono font-bold ${statusConfig.color}`}>
                          {statusConfig.label}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-500 uppercase">Confidence</span>
                        <p className={`text-lg font-mono font-bold ${
                          currentAED.ai_confidence >= 0.85 ? 'text-emerald-400' :
                          currentAED.ai_confidence >= 0.70 ? 'text-amber-400' : 'text-red-400'
                        }`}>
                          {formatConfidence(currentAED.ai_confidence)}
                        </p>
                      </div>
                    </div>
                    
                    {analysis?.reason && (
                      <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                        <p className="text-sm text-slate-300 italic">"{analysis.reason}"</p>
                      </div>
                    )}
                  </div>

                  {/* Feature Breakdown */}
                  <div className="glass rounded-lg p-4">
                    <h3 className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-4">
                      Detection Breakdown
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2 border-b border-slate-800/50">
                        <span className="text-sm text-slate-300">AED Present</span>
                        <div className="flex items-center gap-2">
                          <span className={analysis?.detected_aed ? 'text-emerald-400' : 'text-red-400'}>
                            {analysis?.detected_aed ? 'Yes' : 'No'}
                          </span>
                          <span className="text-xs font-mono text-slate-500">
                            {formatConfidence(analysis?.aed_present_confidence || 0)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between py-2 border-b border-slate-800/50">
                        <span className="text-sm text-slate-300">LED Visible</span>
                        <div className="flex items-center gap-2">
                          <span className={analysis?.led_visible ? 'text-emerald-400' : 'text-amber-400'}>
                            {analysis?.led_visible ? 'Yes' : 'No'}
                          </span>
                          <span className="text-xs font-mono text-slate-500">
                            {formatConfidence(analysis?.led_visible_confidence || 0)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between py-2 border-b border-slate-800/50">
                        <span className="text-sm text-slate-300">LED Color</span>
                        <div className="flex items-center gap-2">
                          <span className={
                            analysis?.led_state === 'green' ? 'text-emerald-400' :
                            analysis?.led_state === 'red' ? 'text-red-400' : 'text-slate-400'
                          }>
                            {analysis?.led_state?.charAt(0).toUpperCase() + analysis?.led_state?.slice(1) || 'Unknown'}
                          </span>
                          <span className="text-xs font-mono text-slate-500">
                            {formatConfidence(analysis?.led_color_confidence || 0)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-slate-300">Expiration Detected</span>
                        <div className="flex items-center gap-2">
                          <span className={expirationConfidence ? 'text-amber-400' : 'text-slate-400'}>
                            {analysis?.reason && /expir|expired|expiration/i.test(analysis.reason)
                              ? analysis.reason
                              : (expirationConfidence ? 'Yes' : 'No text detected')}
                          </span>
                          <span className="text-xs font-mono text-slate-500">
                            {formatConfidence(expirationConfidence)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Confidence Bars - use same values as breakdown so text and bar always match */}
                  <div className="glass rounded-lg p-4">
                    <h3 className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-4">
                      Confidence Scores
                    </h3>
                    <div className="space-y-3">
                      <ConfidenceBar label="AED Detection" value={analysis?.aed_present_confidence || 0} />
                      <ConfidenceBar label="LED Visibility" value={analysis?.led_visible_confidence || 0} />
                      <ConfidenceBar label="LED Color" value={analysis?.led_color_confidence || 0} />
                      <ConfidenceBar label="Expiration Check" value={expirationConfidence} />
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="action" className="space-y-4 mt-4">
                  {/* Comment Field */}
                  <div className="glass rounded-lg p-4">
                    <label className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block">
                      Comment {overrideStatus && <span className="text-red-400">*</span>}
                    </label>
                    <Textarea
                      data-testid="action-comment"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Add notes about your decision..."
                      className="bg-slate-900/50 border-slate-700 min-h-[80px]"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="glass rounded-lg p-4 space-y-4">
                    {/* Confirm */}
                    <Button
                      data-testid="confirm-btn"
                      onClick={handleConfirm}
                      disabled={processing}
                      className="w-full bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 h-12"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Confirm AI Status
                    </Button>

                    {/* Override */}
                    <div className="border-t border-slate-800 pt-4">
                      <label className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block">
                        Override Status
                      </label>
                      <div className="flex gap-2">
                        <Select value={overrideStatus} onValueChange={setOverrideStatus}>
                          <SelectTrigger data-testid="override-status-select" className="flex-1 bg-slate-900/50 border-slate-700">
                            <SelectValue placeholder="Select new status" />
                          </SelectTrigger>
                          <SelectContent>
                            {AED_STATUS_ORDER.map((key) => {
                              const value = AED_STATUSES[key];
                              const config = STATUS_CONFIG[key] || STATUS_CONFIG.UNKNOWN;
                              return (
                                <SelectItem key={key} value={value}>
                                  <div className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${config.dotColor}`} />
                                    {config.label}
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        <Button
                          data-testid="override-btn"
                          onClick={handleOverride}
                          disabled={processing || !overrideStatus}
                          variant="outline"
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                        >
                          <ArrowRight className="w-4 h-4 mr-1" />
                          Override
                        </Button>
                      </div>
                    </div>

                    {/* Escalate */}
                    <Button
                      data-testid="escalate-btn"
                      onClick={handleEscalate}
                      disabled={processing}
                      variant="outline"
                      className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Escalate to Field Team
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
